import { DndContext, DragOverlay } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Box, Stack } from "@mui/material";
import type { FC, ReactNode } from "react";
import { MenuItemType } from "../../../../features/list/api/updateMenuItemOrder";

import type { Folder, Workflow } from "@/types/resources";
import { useMenuData } from "../hooks/useMenuData";
import { DroppableContainer } from "./DroppableContainer";
import { SortableContainer } from "./SortableContainer";
import {
  ROOT_CONTAINER_ID,
  UNASSIGNED_FOLDER_ID,
  useWorkflowDnD,
} from "./useWorkflowDnD";

type Props = {
  folders: Folder[];
  workflows: Workflow[];
  workflowItemRenderer: (args: { workflow: Workflow }) => ReactNode;
  folderItemRenderer: (args: {
    folder: {
      id: string;
      name: string;
      workflows: Workflow[];
    };
    defaultOpen?: boolean;
    workflowItemRenderer: (args: { workflow: Workflow }) => ReactNode;
  }) => ReactNode;
};

export const WorkflowList: FC<Props> = ({
  folders,
  workflows,
  workflowItemRenderer,
  folderItemRenderer,
}) => {
  const { changing, handleUpdateMenuOrder } = useMenuData();
  const {
    dndProps,
    activeWorkflow,
    workflows: optimisticWorkflows,
    folderIds,
  } = useWorkflowDnD({
    folders,
    unassignedWorkflows: workflows,
    onDragEnd: async (args) => {
      if (args.kind === "folder") {
        await handleUpdateMenuOrder({
          itemType: MenuItemType.FOLDER,
          itemId: args.id,
          // backend でもっている order は 1 始まりなので +1 する
          order: args.index + 1,
          parentFolderId: null,
        });
        return;
      }

      const parentFolderId =
        args.containerId === UNASSIGNED_FOLDER_ID ? null : args.containerId;

      await handleUpdateMenuOrder({
        itemType: MenuItemType.WORKFLOW,
        itemId: args.id,
        // backend でもっている order は 1 始まりなので +1 する
        order: args.index + 1,
        parentFolderId,
      });
    },
  });

  const unssigned_workflows =
    optimisticWorkflows[UNASSIGNED_FOLDER_ID]?.workflows ?? [];

  return (
    <DndContext {...dndProps}>
      {activeWorkflow !== null && (
        <DragOverlay>
          {activeWorkflow.kind === "folder" ? (
            <SortableContainer itemId={activeWorkflow.folder.id}>
              {folderItemRenderer({
                folder: activeWorkflow.folder,
                defaultOpen: false,
                workflowItemRenderer,
              })}
            </SortableContainer>
          ) : activeWorkflow.kind === "workflow" ? (
            <SortableContainer itemId={activeWorkflow.workflow.id}>
              {workflowItemRenderer({ workflow: activeWorkflow.workflow })}
            </SortableContainer>
          ) : null}
        </DragOverlay>
      )}
      <Stack spacing={1}>
        <SortableContext
          id={ROOT_CONTAINER_ID}
          items={folderIds}
          strategy={verticalListSortingStrategy}
          disabled={changing}
        >
          {folderIds.map((folderId) => {
            const folder = optimisticWorkflows[folderId];
            if (folder === undefined) return null;
            return (
              <DroppableContainer key={folder.id} containerId={folder.id}>
                <SortableContainer itemId={folder.id} activeOpacity={0.5}>
                  <SortableContext
                    items={folder.workflows}
                    strategy={verticalListSortingStrategy}
                    id={folder.id}
                    disabled={activeWorkflow?.kind === "folder" || changing}
                  >
                    <DroppableContainer containerId={folder.id}>
                      {folderItemRenderer({
                        folder,
                        workflowItemRenderer: ({ workflow }) => (
                          <SortableContainer itemId={workflow.id}>
                            {workflowItemRenderer({ workflow })}
                          </SortableContainer>
                        ),
                      })}
                    </DroppableContainer>
                  </SortableContext>
                </SortableContainer>
              </DroppableContainer>
            );
          })}
        </SortableContext>

        <SortableContext
          items={unssigned_workflows}
          strategy={verticalListSortingStrategy}
          id={UNASSIGNED_FOLDER_ID}
          disabled={changing}
        >
          <DroppableContainer containerId={UNASSIGNED_FOLDER_ID}>
            {unssigned_workflows.map((workflow) => (
              <SortableContainer key={workflow.id} itemId={workflow.id}>
                {workflowItemRenderer({ workflow })}
              </SortableContainer>
            ))}
            {/* ワークフローがない場合、コンテナとの衝突検知ができるようにあり程度の領域を確保しておく */}
            {unssigned_workflows.length === 0 && (
              <Box sx={{ height: "10px" }} />
            )}
          </DroppableContainer>
        </SortableContext>
      </Stack>
    </DndContext>
  );
};
